import React, { PropsWithChildren } from "react";
import { useTranslation } from "i18n";
import { checkIsYp } from "shared-auth";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { dataTestIds } from "data-testids";
import { ReactComponent as SearchIcon } from "../../../../../images/svg/icons/search.svg";
import { ReactComponent as CogIcon } from "../../../../../images/svg/icons/cog-settings.svg";
import { ReactComponent as HelpIcon } from "../../../../../images/svg/icons/help-question.svg";
import { ReactComponent as UserIcon } from "../../../../../images/svg/icons/user-outline.svg";
import { ReactComponent as CameraIcon } from "../../../../../images/svg/icons/media-camera.svg";
import { ReactComponent as BenefitsIcon } from "../../../../../images/svg/icons/things-events-ticket.svg";
import GenericAppShell, { GenericAppShellProps } from "../GenericAppShell";
import { ThemeContextValues } from "../NavThemeContext";
import LeftHandNavItem from "../LeftHandNavItem";
import FilmmakersLeftHandNavItem from "./FilmmakersLeftHandNavItem";
import QuickActionButton from "../QuickActionButton";
import NavItem from "../NavItem";
import { Pill } from "../../../../Atoms/Pill/Pill";
import { useGetAvatar } from "../../hooks/useGetAvatar";
import { performerViewPinPath } from "../../constants";

const PerformerAppShell = ({
  footerComponent,
  hideFooter,
  serviceBannerPortalNone,
  children,
}: PropsWithChildren<
  Pick<
    GenericAppShellProps,
    "hideFooter" | "footerComponent" | "serviceBannerPortalNone"
  >
>) => {
  const isMediaPageConsolidationEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.ProfileMediaPageConsolidation
  );
  const { t } = useTranslation();
  const isYp = checkIsYp();
  const img = useGetAvatar();

  return (
    <GenericAppShell
      theme={ThemeContextValues.Performer}
      hideFooter={hideFooter}
      footerComponent={footerComponent}
      avatar={img}
      serviceBannerPortalNone={serviceBannerPortalNone}
      leftHandNavItems={
        <>
          {!isYp && (
            <>
              <LeftHandNavItem to="/jobs/matching-my-filters" icon={SearchIcon}>
                {t("common:nav.links.jobsFeed.filters")}
              </LeftHandNavItem>
              <LeftHandNavItem to="/jobs/all-paid" isSubNav>
                {t("common:nav.links.paidJobs")}
              </LeftHandNavItem>
              <LeftHandNavItem to="/jobs/all-opportunities" isSubNav>
                {t("common:nav.links.allJobs")}
              </LeftHandNavItem>
            </>
          )}
          <LeftHandNavItem to="/jobs/self-tape-requests" isSubNav>
            {t("common:nav.links.mediaRequests")}
            <Pill className="bg-blue-sky-2 ml-1.5" theme="light" size="md">
              {t("common:nav.links.pill.beta")}
            </Pill>
          </LeftHandNavItem>
          {!isYp && (
            <LeftHandNavItem to="/settings/jobs-feed" isSubNav>
              {t("common:nav.links.jobsFeedSettings")}
            </LeftHandNavItem>
          )}
          <FilmmakersLeftHandNavItem />

          <LeftHandNavItem icon={UserIcon} to="/profile?edit=true">
            {t("common:nav.links.editProfile")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="/profile/myviewpin"
            nestedPaths={[performerViewPinPath]}
            isSubNav
          >
            {t("common:nav.links.viewPublicProfile")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/profile?action=email" isSubNav>
            {t("common:nav.links.emailUsersProfile")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/profile?action=print" isSubNav>
            {t("common:nav.links.printUsersProfile")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/profile/linktoprofile" isSubNav>
            {t("common:nav.links.usersProfileLink")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/profile/contacts" isSubNav>
            {t("common:nav.links.representation")}
          </LeftHandNavItem>

          <LeftHandNavItem to="/profile/photos" icon={CameraIcon}>
            {t("common:nav.links.myPhotos")}
          </LeftHandNavItem>
          {isMediaPageConsolidationEnabled ? (
            <LeftHandNavItem to="/profile/media" isSubNav>
              {t("common:nav.links.myMedia")}
            </LeftHandNavItem>
          ) : (
            <>
              <LeftHandNavItem to="/profile/media/video" isSubNav>
                {t("common:nav.links.myVideo")}
              </LeftHandNavItem>
              <LeftHandNavItem to="/profile/media/audio" isSubNav>
                {t("common:nav.links.myAudio")}
              </LeftHandNavItem>
            </>
          )}

          <LeftHandNavItem
            to="https://www.spotlight.com/the-small-print/what-we-offer-our-members/"
            icon={BenefitsIcon}
          >
            {t("common:nav.links.benefits")}
          </LeftHandNavItem>

          <LeftHandNavItem
            to="https://www.spotlight.com/members/spotlight-performer-events/"
            isSubNav
          >
            {t("common:nav.links.events")}
          </LeftHandNavItem>
          {!isYp && (
            <LeftHandNavItem
              to="https://www.spotlight.com/the-small-print/wellbeing-in-the-arts-terms/"
              isSubNav
            >
              {t("common:nav.links.membershipAssistanceProgramme")}
            </LeftHandNavItem>
          )}
          <LeftHandNavItem to="/discounts/members" isSubNav>
            {t("common:nav.links.discounts")}
          </LeftHandNavItem>

          <LeftHandNavItem to="https://app.spotlight.com/contacts" isSubNav>
            {t("common:nav.links.contactsListings")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/news-and-advice/"
            isSubNav
          >
            {t("common:nav.links.newsAndAdvice")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/industry-resources/"
            isSubNav
          >
            {t("common:nav.links.industryResources")}
          </LeftHandNavItem>
        </>
      }
      dynamicHeaderContent={
        !isYp && (
          <QuickActionButton
            label={t("common:nav.links.jobsFeed")}
            linkUrl="/jobs/matching-my-filters"
          />
        )
      }
      userMenuItems={
        <>
          <NavItem
            to="/myhome/account-settings"
            icon={CogIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.accountSettings")}
          </NavItem>
          <NavItem
            to="/myhome/account-settings/my-subscription"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.usersSubscription")}
          </NavItem>
          {!isYp && (
            <NavItem
              to="/settings"
              data-testid={
                dataTestIds.componentLibrary[
                  "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
                ]
              }
              isSubNav
            >
              {t("common:nav.links.emailSettings")}
            </NavItem>
          )}
          <NavItem
            to="/artists/privacy.aspx"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.privacySettings")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/help-and-faqs/"
            icon={HelpIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.helpAndTutorials")}
          </NavItem>
          <NavItem
            to="https://support.spotlight.com"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.support")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/rooms-and-studios/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.roomAndStudiosHire")}
          </NavItem>
        </>
      }
    >
      {children}
    </GenericAppShell>
  );
};

export default PerformerAppShell;
